import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'

// Static imports — must be at top of file
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import ModuleGuard from './components/ModuleGuard'
import SettingsLayout from './pages/client/Settings/SettingsLayout'
import HelpLayout from './pages/common/Help/HelpLayout'
import LoginPage from './pages/common/LoginPage'
import SuperAdminLogin from './pages/common/SuperAdminLogin'
import ProtectedRoute from './components/ProtectedRoute'
import HomeRedirect from './components/HomeRedirect'

// ── Client Pages ──────────────────────────────────────────────────────────────
const Dashboard = lazy(() => import('./pages/client/Dashboard'))
const AIIntelligenceStudio = lazy(() => import('./pages/client/AI/AIIntelligenceStudio'))
const ModuleDashboard = lazy(() => import('./pages/client/ModuleDashboard'))
const PurchaseOrders = lazy(() => import('./pages/client/PurchaseOrders/PurchaseOrders'))
const PurchaseOrderDetail = lazy(() => import('./pages/client/PurchaseOrders/PurchaseOrderDetail'))
const PurchaseOrderForm = lazy(() => import('./pages/client/PurchaseOrders/PurchaseOrderForm'))
const Inventory = lazy(() => import('./pages/client/Inventory/Inventory'))
const InventoryDetail = lazy(() => import('./pages/client/Inventory/InventoryDetail'))
const InventoryForm = lazy(() => import('./pages/client/Inventory/InventoryForm'))
const Invoices = lazy(() => import('./pages/client/Invoices/Invoices'))
const InvoiceDetail = lazy(() => import('./pages/client/Invoices/InvoiceDetail'))
const InvoiceForm = lazy(() => import('./pages/client/Invoices/InvoiceForm'))
const Payments = lazy(() => import('./pages/client/Payments/Payments'))
const PaymentForm = lazy(() => import('./pages/client/Payments/PaymentForm'))
const PaymentDetail = lazy(() => import('./pages/client/Payments/PaymentDetail'))
const UsageLogs = lazy(() => import('./pages/client/UsageLogs'))
const Analytics = lazy(() => import('./pages/client/Analytics'))
const GRC = lazy(() => import('./pages/client/GRC'))
const ApprovalWorkflow = lazy(() => import('./pages/client/Approval/ApprovalWorkflow'))
const ApprovalCenter = lazy(() => import('./pages/client/Approval/ApprovalCenter'))
const ApprovalInbox = lazy(() => import('./pages/client/Approval/ApprovalInbox'))

// ── Client Sub-Modules ────────────────────────────────────────────────────────
const RequisitionList = lazy(() => import('./pages/client/Requisition/RequisitionList'))
const RequisitionForm = lazy(() => import('./pages/client/Requisition/RequisitionForm'))
const RequisitionDetail = lazy(() => import('./pages/client/Requisition/RequisitionDetail'))
const RFxList = lazy(() => import('./pages/client/RFx/RFxList'))
const RFxForm = lazy(() => import('./pages/client/RFx/RFxForm'))
const RFxDetail = lazy(() => import('./pages/client/RFx/RFxDetail'))
const QuotationList = lazy(() => import('./pages/client/Quotation/QuotationList'))
const QuotationForm = lazy(() => import('./pages/client/Quotation/QuotationForm'))
const QuotationDetail = lazy(() => import('./pages/client/Quotation/QuotationDetail'))
const GRNList = lazy(() => import('./pages/client/GRN/GRNList'))
const GRNForm = lazy(() => import('./pages/client/GRN/GRNForm'))
const GRNDetail = lazy(() => import('./pages/client/GRN/GRNDetail'))

// ── Finance ───────────────────────────────────────────────────────────────────
const GeneralLedgerList = lazy(() => import('./pages/client/Finance/GeneralLedgerList'))
const GeneralLedgerForm = lazy(() => import('./pages/client/Finance/GeneralLedgerForm'))
const AccountsPayableList = lazy(() => import('./pages/client/Finance/AccountsPayableList'))
const AccountsPayableForm = lazy(() => import('./pages/client/Finance/AccountsPayableForm'))
const AccountsReceivableList = lazy(() => import('./pages/client/Finance/AccountsReceivableList'))
const AccountsReceivableForm = lazy(() => import('./pages/client/Finance/AccountsReceivableForm'))
const CashBankList = lazy(() => import('./pages/client/Finance/CashBankList'))
const CashBankForm = lazy(() => import('./pages/client/Finance/CashBankForm'))
const TaxAccountingList = lazy(() => import('./pages/client/Finance/TaxAccountingList'))
const TaxAccountingForm = lazy(() => import('./pages/client/Finance/TaxAccountingForm'))
const GeneralLedgerAccountForm = lazy(() => import('./pages/client/Finance/GeneralLedgerAccountForm'))

// ── Budget ────────────────────────────────────────────────────────────────────
const BudgetDashboard = lazy(() => import('./pages/client/Budget/BudgetDashboard'))
const BudgetPlanning = lazy(() => import('./pages/client/Budget/BudgetPlanning'))
const BudgetForm = lazy(() => import('./pages/client/Budget/BudgetForm'))
const BudgetDetail = lazy(() => import('./pages/client/Budget/BudgetDetail'))

// ── Budget Control ────────────────────────────────────────────────────────────
const BudgetControlDashboard = lazy(() => import('./pages/client/BudgetControl/BudgetControlDashboard'))
const BudgetControlRules = lazy(() => import('./pages/client/BudgetControl/BudgetControlRules'))
const BudgetControlRuleForm = lazy(() => import('./pages/client/BudgetControl/BudgetControlRuleForm'))
const BudgetOverrides = lazy(() => import('./pages/client/BudgetControl/BudgetOverrides'))
const CommitmentRegister = lazy(() => import('./pages/client/BudgetControl/CommitmentRegister'))
const ObligationRegister = lazy(() => import('./pages/client/BudgetControl/ObligationRegister'))
const BudgetAvailabilityLogs = lazy(() => import('./pages/client/BudgetControl/BudgetAvailabilityLogs'))

// ── POS & Multi-Branch ────────────────────────────────────────────────────────
const POSDashboard = lazy(() => import('./pages/client/POS/POSDashboard'))
const POSRegister = lazy(() => import('./pages/client/POS/POSRegister'))
const POSTerminals = lazy(() => import('./pages/client/POS/POSTerminals'))
const BranchesAndStores = lazy(() => import('./pages/client/POS/BranchesAndStores'))
const StockTransfers = lazy(() => import('./pages/client/POS/StockTransfers'))

// ── Settings (Client) ─────────────────────────────────────────────────────────
const UserManagement = lazy(() => import('./pages/client/Settings/UserManagement'))
const UserForm = lazy(() => import('./pages/client/Settings/UserForm'))
const VendorManagement = lazy(() => import('./pages/client/Settings/VendorManagement'))
const VendorForm = lazy(() => import('./pages/client/Settings/VendorForm'))
const ClientManagement = lazy(() => import('./pages/client/Settings/ClientManagement'))
const ClientForm = lazy(() => import('./pages/client/Settings/ClientForm'))
const ContractManagement = lazy(() => import('./pages/client/Settings/ContractManagement'))
const ContractForm = lazy(() => import('./pages/client/Settings/ContractForm'))
const RoleManagement = lazy(() => import('./pages/client/Settings/RoleManagement'))
const DesignationSettings = lazy(() => import('./pages/client/Settings/DesignationSettings'))
const DepartmentSettings = lazy(() => import('./pages/client/Settings/DepartmentSettings'))
const BusinessUnitSettings = lazy(() => import('./pages/client/Settings/BusinessUnitSettings'))
const SystemSettings = lazy(() => import('./pages/client/Settings/SystemSettings'))
const BillingUsage = lazy(() => import('./pages/client/Settings/BillingUsage'))
const LicenseManagement = lazy(() => import('./pages/client/Settings/LicenseManagement'))
const SubscriptionManagement = lazy(() => import('./pages/client/Settings/SubscriptionManagement'))
const AdminControlTower = lazy(() => import('./pages/superadmin/ControlTower/AdminControlTower'))
const BusinessConfiguration = lazy(() => import('./pages/client/Settings/BusinessConfiguration'))

// ── SuperAdmin Dedicated Pages ────────────────────────────────────────────────
const SuperAdminClients = lazy(() => import('./pages/superadmin/Clients/ClientManagement'))
const SuperAdminClientForm = lazy(() => import('./pages/superadmin/Clients/ClientForm'))
const SuperAdminLicenses = lazy(() => import('./pages/superadmin/Licenses/LicenseManagement'))
const SuperAdminRoles = lazy(() => import('./pages/superadmin/Roles/RoleManagement'))
const SuperAdminMonitoring = lazy(() => import('./pages/superadmin/Monitoring/PlatformMonitoring'))
const SuperAdminSystemSettings = lazy(() => import('./pages/superadmin/System/SystemSettings'))
const PlatformAudit = lazy(() => import('./pages/superadmin/Audit/PlatformAudit'))
const AIAuditManagement = lazy(() => import('./pages/superadmin/Audit/AIAuditManagement'))

// ── Vendor Pages ──────────────────────────────────────────────────────────────
const VendorDashboard = lazy(() => import('./pages/vendor/VendorDashboard'))
const VendorRFxDetail = lazy(() => import('./pages/vendor/VendorRFxDetail'))
const MyBids = lazy(() => import('./pages/vendor/MyBids'))
const VendorRFxList = lazy(() => import('./pages/vendor/VendorRFxList'))
const RFxMatchingEngine = lazy(() => import('./pages/vendor/RFxMatchingEngine'))
const VendorPOList = lazy(() => import('./pages/vendor/VendorPO/VendorPOList'))
const VendorPODetail = lazy(() => import('./pages/vendor/VendorPO/VendorPODetail'))
const VendorPOForm = lazy(() => import('./pages/vendor/VendorPO/VendorPOForm'))
const VendorDelivery = lazy(() => import('./pages/vendor/VendorPO/VendorDelivery'))
const VendorShipmentForm = lazy(() => import('./pages/vendor/VendorPO/VendorShipmentForm'))
const VendorShipmentList = lazy(() => import('./pages/vendor/VendorPO/VendorShipmentList'))
const VendorInvoices = lazy(() => import('./pages/vendor/VendorInvoices'))
const VendorInvoiceForm = lazy(() => import('./pages/vendor/VendorInvoiceForm'))
const VendorInvoiceDetail = lazy(() => import('./pages/vendor/VendorInvoiceDetail'))
const VendorPayments = lazy(() => import('./pages/vendor/VendorPayments'))
const VendorPaymentDetail = lazy(() => import('./pages/vendor/VendorPaymentDetail'))
const VendorPaymentForm = lazy(() => import('./pages/vendor/VendorPaymentForm'))
const VendorCompanyProfile = lazy(() => import('./pages/vendor/VendorCompanyProfile'))
const VendorSupport = lazy(() => import('./pages/vendor/VendorSupport'))

// ── Common Pages ──────────────────────────────────────────────────────────────
const Profile = lazy(() => import('./pages/common/Profile'))
const PrivacyPolicy = lazy(() => import('./pages/common/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/common/TermsOfService'))
const HelpHome = lazy(() => import('./pages/common/Help/HelpHome'))
const HelpArticle = lazy(() => import('./pages/common/Help/HelpArticle'))
const HelpSearch = lazy(() => import('./pages/common/Help/HelpSearch'))
const TicketingLayout = lazy(() => import('./pages/common/Tickets/TicketingLayout'))
const TicketList = lazy(() => import('./pages/common/Tickets/TicketList'))
const TicketSubmit = lazy(() => import('./pages/common/Tickets/TicketSubmit'))
const TicketDetail = lazy(() => import('./pages/common/Tickets/TicketDetail'))
const SupportDashboard = lazy(() => import('./pages/common/Tickets/SupportDashboard'))
const TicketAuditActivity = lazy(() => import('./pages/common/Tickets/TicketAuditActivity'))
const PortalSelection = lazy(() => import('./pages/common/PortalSelection'))
const CommonDashboard = lazy(() => import('./pages/common/CommonDashboard'))
const ForgotPasswordPage = lazy(() => import('./pages/common/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/common/ResetPasswordPage'))

// ── Reports ───────────────────────────────────────────────────────────────────
const ReportHub = lazy(() => import('./pages/client/Reports/ReportHub'))
const ReportPage = lazy(() => import('./pages/client/Reports/ReportPage'))
const VendorReportHub = lazy(() => import('./pages/vendor/Reports/VendorReportHub'))
const VendorReportPage = lazy(() => import('./pages/vendor/Reports/VendorReportPage'))

// ─────────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="Loading application..." />
      </div>
    }>
      <Routes>
        {/* ── Public Routes ── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/superadmin/login" element={<SuperAdminLogin />} />
        <Route path="/login/superadmin" element={<Navigate to="/superadmin/login" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* ── Help Center (independent layout) ── */}
        <Route path="/help" element={<HelpLayout />}>
          <Route index element={<HelpHome />} />
          <Route path="search" element={<HelpSearch />} />
          <Route path=":slug" element={<HelpArticle />} />
        </Route>

        {/* ── Semi-Protected Routes ── */}
        <Route path="/common-dashboard" element={<ProtectedRoute><CommonDashboard /></ProtectedRoute>} />
        <Route path="/portal-selection" element={<ProtectedRoute><PortalSelection /></ProtectedRoute>} />

        {/* ── Ticketing (protected, own layout) ── */}
        <Route path="/tickets" element={<ProtectedRoute><TicketingLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="my" element={<TicketList />} />
          <Route path="dashboard" element={<SupportDashboard />} />
          <Route path="support" element={<SupportDashboard />} />
          <Route path="audit" element={<TicketAuditActivity />} />
          <Route path="new" element={<TicketSubmit />} />
          <Route path=":id" element={<TicketDetail />} />
        </Route>

        {/* ── Main Application (pathless layout — never intercepts public routes) ── */}
        <Route element={<ProtectedRoute><Layout><Outlet /></Layout></ProtectedRoute>}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/executive-dashboard" element={<Navigate to="/dashboard" replace />} />

          {/* Module Dashboards */}
          <Route path="/buy" element={<ModuleDashboard />} />
          <Route path="/finance" element={<ModuleDashboard />} />
          <Route path="/branches" element={<ModuleDashboard />} />
          <Route path="/contracts" element={<ModuleDashboard />} />
          <Route path="/ai" element={<AIIntelligenceStudio />} />

          <Route path="/approvals" element={<ApprovalCenter />} />
          <Route path="/approvals/inbox" element={<ApprovalInbox />} />
          <Route path="/approvals/workflows" element={<ApprovalWorkflow />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Budget */}
          <Route element={<ModuleGuard module="budget"><Outlet /></ModuleGuard>}>
            <Route path="/budget" element={<Navigate to="/budget/overview" replace />} />
            <Route path="/budget/overview" element={<BudgetDashboard />} />
            <Route path="/budget/planning" element={<BudgetPlanning />} />
            <Route path="/budget/new" element={<BudgetForm />} />
            <Route path="/budget/:id" element={<BudgetDetail />} />
            <Route path="/budget/:id/edit" element={<BudgetForm />} />
            <Route path="/budget-spend" element={<Navigate to="/budget/overview" replace />} />
            <Route path="/budget-control/dashboard" element={<BudgetControlDashboard />} />
            <Route path="/budget-control/rules" element={<BudgetControlRules />} />
            <Route path="/budget-control/rules/new" element={<BudgetControlRuleForm />} />
            <Route path="/budget-control/rules/:id" element={<BudgetControlRuleForm />} />
            <Route path="/budget-control/overrides" element={<BudgetOverrides />} />
            <Route path="/budget-control/commitments" element={<CommitmentRegister />} />
            <Route path="/budget-control/obligations" element={<ObligationRegister />} />
            <Route path="/budget-control/availability-logs" element={<BudgetAvailabilityLogs />} />
          </Route>

          <Route path="/analytics" element={<Analytics />} />
          <Route path="/integration" element={<Navigate to="/platform" replace />} />

          <Route element={<ModuleGuard module="grc"><Outlet /></ModuleGuard>}>
            <Route path="/grc" element={<GRC />} />
          </Route>

          {/* Reports */}
          <Route path="/reports" element={<ReportHub />} />
          <Route path="/reports/:category/:slug" element={<ReportPage />} />

          {/* Procurement */}
          <Route element={<ModuleGuard module="procurement"><Outlet /></ModuleGuard>}>
            <Route path="/purchase-orders" element={<PurchaseOrders />} />
            <Route path="/purchase-orders/new" element={<PurchaseOrderForm />} />
            <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail />} />
            <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderForm />} />
            <Route path="/requisitions" element={<RequisitionList />} />
            <Route path="/requisitions/new" element={<RequisitionForm />} />
            <Route path="/requisitions/:id" element={<RequisitionDetail />} />
            <Route path="/requisitions/:id/edit" element={<RequisitionForm />} />
            <Route path="/rfx" element={<RFxList />} />
            <Route path="/rfx/new" element={<RFxForm />} />
            <Route path="/rfx/:id" element={<RFxDetail />} />
            <Route path="/rfx/:id/edit" element={<RFxForm />} />
            <Route path="/quotations" element={<QuotationList />} />
            <Route path="/quotations/new" element={<QuotationForm />} />
            <Route path="/quotations/:id" element={<QuotationDetail />} />
            <Route path="/quotations/:id/edit" element={<QuotationForm />} />
          </Route>

          {/* Vendor */}
          <Route element={<ModuleGuard module="vendor_portal"><Outlet /></ModuleGuard>}>
            <Route path="/vendor/dashboard" element={<VendorDashboard />} />
            <Route path="/vendor/bids" element={<MyBids />} />
            <Route path="/vendor/rfx" element={<VendorRFxList />} />
            <Route path="/vendor/rfx/:id" element={<VendorRFxDetail />} />
            <Route path="/vendor/rfx-matching" element={<RFxMatchingEngine />} />
            <Route path="/vendor-pos" element={<VendorPOList />} />
            <Route path="/vendor-pos/ack" element={<Navigate to="/vendor-pos" replace />} />
            <Route path="/vendor-pos/new" element={<Navigate to="/vendor-pos" replace />} />
            <Route path="/vendor-pos/:id" element={<VendorPODetail />} />
            <Route path="/vendor-pos/:id/edit" element={<VendorPOForm />} />
            <Route path="/vendor-pos/:id/receive" element={<VendorDelivery />} />
            <Route path="/vendor-pos/:id/shipments/new" element={<VendorShipmentForm />} />
            <Route path="/vendor/shipments" element={<VendorShipmentList />} />
            <Route path="/vendor/invoices" element={<Navigate to="/vendor/finance/invoices" replace />} />
            <Route path="/vendor/finance/invoices" element={<VendorInvoices />} />
            <Route path="/vendor/finance/invoices/new" element={<VendorInvoiceForm />} />
            <Route path="/vendor/finance/invoices/:id" element={<VendorInvoiceDetail />} />
            <Route path="/vendor/finance/invoices/:id/edit" element={<VendorInvoiceForm />} />
            <Route path="/vendor/payments" element={<Navigate to="/vendor/finance/payments" replace />} />
            <Route path="/vendor/finance/payments" element={<VendorPayments />} />
            <Route path="/vendor/finance/payments/new" element={<VendorPaymentForm />} />
            <Route path="/vendor/finance/payments/:id" element={<VendorPaymentDetail />} />
            <Route path="/vendor/finance/payments/:id/edit" element={<VendorPaymentForm />} />
            <Route path="/vendor/profile" element={<VendorCompanyProfile />} />
            <Route path="/vendor/documents" element={<VendorDashboard />} />
            <Route path="/vendor/messages" element={<VendorDashboard />} />
            <Route path="/vendor/support" element={<VendorSupport />} />
            <Route path="/vendor/reports" element={<VendorReportHub />} />
            <Route path="/vendor/reports/:slug" element={<VendorReportPage />} />
          </Route>

          {/* Inventory & GRN */}
          <Route element={<ModuleGuard module="inventory"><Outlet /></ModuleGuard>}>
            <Route path="/order-receipts" element={<GRNList />} />
            <Route path="/order-receipts/new" element={<GRNForm />} />
            <Route path="/order-receipts/:id" element={<GRNDetail />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/inventory/new" element={<InventoryForm />} />
            <Route path="/inventory/:id" element={<InventoryDetail />} />
            <Route path="/inventory/:id/edit" element={<InventoryForm />} />
          </Route>

          {/* POS */}
          <Route element={<ModuleGuard module="pos"><Outlet /></ModuleGuard>}>
            <Route path="/pos" element={<POSDashboard />} />
            <Route path="/pos/dashboard" element={<POSDashboard />} />
            <Route path="/pos/register" element={<POSRegister />} />
            <Route path="/pos/terminals" element={<POSTerminals />} />
            <Route path="/pos/branches-stores" element={<BranchesAndStores />} />
            <Route path="/pos/transfers" element={<StockTransfers />} />
          </Route>

          {/* Finance */}
          <Route element={<ModuleGuard module="finance"><Outlet /></ModuleGuard>}>
            <Route path="/finance/accounting/general-ledger" element={<GeneralLedgerList />} />
            <Route path="/finance/accounting/general-ledger/new" element={<GeneralLedgerForm />} />
            <Route path="/finance/accounting/general-ledger/:id/edit" element={<GeneralLedgerForm />} />
            <Route path="/finance/accounting/general-ledger/accounts/new" element={<GeneralLedgerAccountForm />} />
            <Route path="/finance/accounting/accounts-payable" element={<AccountsPayableList />} />
            <Route path="/finance/accounting/accounts-payable/new" element={<AccountsPayableForm />} />
            <Route path="/finance/accounting/accounts-payable/:id/edit" element={<AccountsPayableForm />} />
            <Route path="/finance/accounting/accounts-receivable" element={<AccountsReceivableList />} />
            <Route path="/finance/accounting/accounts-receivable/new" element={<AccountsReceivableForm />} />
            <Route path="/finance/accounting/accounts-receivable/:id/edit" element={<AccountsReceivableForm />} />
            <Route path="/finance/accounting/cash-bank" element={<CashBankList />} />
            <Route path="/finance/accounting/cash-bank/new" element={<CashBankForm />} />
            <Route path="/finance/accounting/cash-bank/:id/edit" element={<CashBankForm />} />
            <Route path="/finance/accounting/tax" element={<TaxAccountingList />} />
            <Route path="/finance/accounting/tax/new" element={<TaxAccountingForm />} />
            <Route path="/finance/accounting/tax/:id/edit" element={<TaxAccountingForm />} />
            <Route path="/finance/accounting/invoices" element={<Invoices />} />
            <Route path="/finance/accounting/invoices/new" element={<InvoiceForm />} />
            <Route path="/finance/accounting/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/finance/accounting/invoices/:id/edit" element={<InvoiceForm />} />
            <Route path="/finance/accounting/payments" element={<Payments />} />
            <Route path="/finance/accounting/payments/new" element={<PaymentForm />} />
            <Route path="/finance/accounting/payments/:id" element={<PaymentDetail />} />
            <Route path="/finance/accounting/payments/:id/edit" element={<PaymentForm />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/new" element={<InvoiceForm />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/invoices/:id/edit" element={<InvoiceForm />} />
            <Route path="/payments" element={<Payments />} />
          </Route>

          <Route path="/usage-logs" element={<UsageLogs />} />
          <Route path="/profile" element={<Profile />} />

          {/* SuperAdmin Platform Routes */}
          <Route path="/superadmin" element={<Navigate to="/superadmin/control-tower" replace />} />
          <Route path="/superadmin/control-tower" element={<AdminControlTower />} />
          <Route path="/superadmin/settings/control-tower" element={<Navigate to="/superadmin/control-tower" replace />} />
          <Route path="/superadmin/clients" element={<SuperAdminClients />} />
          <Route path="/superadmin/clients/new" element={<SuperAdminClientForm />} />
          <Route path="/superadmin/clients/:id/edit" element={<SuperAdminClientForm />} />
          <Route path="/superadmin/subscriptions" element={<Navigate to="/superadmin/licenses" replace />} />
          <Route path="/superadmin/licenses" element={<SuperAdminLicenses />} />
          <Route path="/superadmin/modules" element={<Navigate to="/superadmin/settings?tab=modules" replace />} />
          <Route path="/superadmin/marketplace" element={<Navigate to="/superadmin/licenses" replace />} />
          <Route path="/superadmin/roles" element={<SuperAdminRoles />} />
          <Route path="/superadmin/monitoring" element={<SuperAdminMonitoring />} />
          <Route path="/superadmin/ai-audit" element={<AIAuditManagement />} />
          <Route path="/superadmin/audit" element={<PlatformAudit />} />
          <Route path="/superadmin/settings" element={<SuperAdminSystemSettings />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="/settings/system?tab=users" replace />} />
            <Route path="users" element={<Navigate to="/settings/system?tab=users" replace />} />
            <Route path="users/new" element={<Navigate to="/settings/system?tab=users&mode=new" replace />} />
            <Route path="vendors" element={<VendorManagement />} />
            <Route path="vendors/new" element={<VendorForm />} />
            <Route path="vendors/:id/edit" element={<VendorForm />} />
            <Route path="clients" element={<SuperAdminClients />} />
            <Route path="clients/new" element={<SuperAdminClientForm />} />
            <Route path="clients/:id/edit" element={<SuperAdminClientForm />} />
            <Route path="contracts" element={<ContractManagement />} />
            <Route path="contracts/new" element={<ContractForm />} />
            <Route path="contracts/:id/edit" element={<ContractForm />} />
            <Route path="roles" element={<RoleManagement />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/new" element={<InventoryForm />} />
            <Route path="inventory/:id" element={<InventoryDetail />} />
            <Route path="inventory/:id/edit" element={<InventoryForm />} />
            <Route path="designations" element={<DesignationSettings />} />
            <Route path="departments" element={<DepartmentSettings />} />
            <Route path="business-units" element={<BusinessUnitSettings />} />
            <Route path="system" element={<SystemSettings />} />
            <Route path="billing" element={<BillingUsage />} />
            <Route path="license" element={<LicenseManagement />} />
            <Route path="subscription" element={<SubscriptionManagement />} />
            <Route path="control-tower" element={<Navigate to="/superadmin/control-tower" replace />} />
            <Route path="business-configuration" element={<BusinessConfiguration />} />
          </Route>

          <Route path="*" element={<HomeRedirect />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
